import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TemplateService } from 'app/Services/template.service';
import { ValidationService } from 'app/Services/Validation/validation.service';
import DataSource from 'devextreme/data/data_source';
import dxDropDownBox, { ValueChangedEvent } from 'devextreme/ui/drop_down_box';
import { ItemClickEvent } from 'devextreme/ui/list';
import { CustomItemCreatingEvent } from 'devextreme/ui/select_box';

type ValidateFn = (params: any) => boolean;
type FormatDisplayValueFn = (value: string) => string;
type DropdownItem = { value: string; displayValue: string };

@Component({
    selector: 'np-dropdown-control',
    templateUrl: './dropdown-control.component.html',
    styleUrls: ['./dropdown-control.component.css'],
    standalone: false
})
export class DropdownControlComponent implements OnInit, OnChanges {
  @ViewChild('dropdown', { static: false }) dropdownComponent!: dxDropDownBox;


  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();

  
  @Input() fieldName: string;
  @Input() systemKey = '-1';
  @Input() elementKey = '-1';
  @Input() readOnly = false;
  @Input() templateView = false;
  @Input() validationGroup: string;

  @Input() validationCallback: ValidateFn = null;
  @Input() items: DropdownItem[] | string[] = [];
  @Input() selectionMode: 'single' | 'multiple' = 'single';

  @Input() translationLabelPrefix: string = null;
  @Input() showRealValue = false;

  
  @Output() onValueChanged = new EventEmitter<ValueChangedEvent>();
  @Output() onCustomItemCreating = new EventEmitter<CustomItemCreatingEvent>();
  @Output() onElementSelected = new EventEmitter<ItemClickEvent<DropdownItem>>();

 
  @Input() formatDisplayValue: FormatDisplayValueFn = (value: string) => {
    return (
      this.translate.instant(
        this.translationLabelPrefix !== null
          ? `${this.translationLabelPrefix}${value}`
          : value
      ) + (this.showRealValue ? ` (${value})` : '')
    );
  };

  customItems: string[] = [];
  opened = false;
  dataSource: DataSource<DropdownItem>;

  constructor(
    public translate: TranslateService,
    public validationService: ValidationService,
    private templateService: TemplateService
  ) { }

  ngOnInit() {
    this.updateDataSource();

    this.templateService.clearPimFieldEmitter.subscribe((pimField) => {
      if (pimField.field === this.fieldName) {
        this.value = '';
        // Neuer Wert -> Parent informieren
        this.valueChange.emit(this.value);
      }
    });

    if (this.validationCallback === null && this.fieldName) {
      this.validationCallback = this.validationService.validateField(
        this.fieldName,
        this.systemKey,
        this.elementKey
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {   
    if (changes.items) {
      this.updateDataSource();
    }
  }

  valueChanged(event: ValueChangedEvent) {
    
    this.value = event.value;

    this.valueChange.emit(this.value);
    this.onValueChanged.emit(event);
  }

  customItemCreating(event: CustomItemCreatingEvent) {
    this.customItems = [...new Set([...this.customItems, event.text])];
    this.updateDataSource();

    this.onCustomItemCreating.emit(event);
  }

  itemClick(event: ItemClickEvent<DropdownItem>) {

    const selectedValue = event.itemData.value;
    this.value = selectedValue;

    
    this.valueChange.emit(this.value);

    this.opened = false;

   
    this.onElementSelected.emit(event);

    this.onValueChanged.emit({
      component: this.dropdownComponent,
      element: event.element,
      value: selectedValue,
      event: event.event
    });
  }

  
  private updateDataSource() {
   if (this.items.every((x) => typeof x === 'string')) {
      const combined = Array.from(new Set([...this.items, ...this.customItems])) as string[];
      this.dataSource = new DataSource(
        combined.map((val) => ({
          value: val,
          displayValue: this.formatDisplayValue(val),
        }))
      );
    } else {     
      this.dataSource = new DataSource(this.items as DropdownItem[]);
    }
  }
}
